/**
 * ProvincieSection — klussen/aanvragen/conversie/omzet per provincie op een
 * Leaflet-choropleth, in dashboard-stijl (card-blue + CSS-variabelen + isDark).
 * Rendert als kaart-blok BINNEN de Pipeline-analyse (die is inklapbaar); geen
 * eigen dropdown. Voedt uit /api/schoorsteenveger/provincie.
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import InfoDot from '../shared/InfoDot';
import { cartoTileUrl } from '../../constants/basemap';
import { getProvincieStats } from '../../utils/schoorsteenvegerQueries';

const METRICS = [
  { k: 'aanvragen', label: 'Aanvragen' },
  { k: 'facturen', label: 'Facturen' },
  { k: 'conversie', label: 'Conversie' },
  { k: 'omzet', label: 'Omzet' },
];
const DIENST_ORDER = ['Schoorsteenvegen', 'Mechanische Ventilatie', 'Camera inspectie', 'Vogelnest', 'Overig'];

const eur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const eurK = (n) => (n >= 10000 ? `€${Math.round(n / 1000)}k` : n >= 1000 ? `€${(n / 1000).toFixed(1).replace('.', ',')}k` : eur(n));
const lerp = (a, b, t) => a.map((x, i) => Math.round(x + (b[i] - x) * t));
const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
const convColor = (c) => {
  if (c == null) return '#94a3b8';
  const t = Math.max(0, Math.min(1, (c - 45) / 35));
  return t < 0.5 ? rgb(lerp([239, 68, 68], [245, 158, 11], t * 2)) : rgb(lerp([245, 158, 11], [34, 197, 94], (t - 0.5) * 2));
};
const ymd = (d) => d.toISOString().slice(0, 10);
// preset -> {startDate,endDate} voor /provincie (leeg = hele historie)
const filtersForPreset = (k) => {
  if (k === 'all') return {};
  if (k === '12m') {
    const d = new Date(); const s = new Date(d); s.setFullYear(s.getFullYear() - 1); s.setDate(s.getDate() + 1);
    return { startDate: ymd(s), endDate: ymd(d) };
  }
  return { startDate: `${k}-01-01`, endDate: `${k}-12-31` }; // een jaartal
};

export default function ProvincieSection({ isDark = false }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const layerRef = useRef(null);
  const [geo, setGeo] = useState(null);
  const [rows, setRows] = useState(null);
  const [metric, setMetric] = useState('aanvragen');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodKey, setPeriodKey] = useState('all');
  const [cFrom, setCFrom] = useState('');
  const [cTo, setCTo] = useState('');

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ''}/data/provincie_pdok.min.json`)
      .then((r) => r.json()).then(setGeo).catch(() => setGeo(null));
  }, []);

  // eigen periodeselectie (los van de dagperiode van het dashboard)
  useEffect(() => {
    let alive = true;
    setLoading(true);
    let f;
    if (cFrom || cTo) {
      const from = cFrom || cTo; const to = cTo || cFrom;
      const [ty, tm] = to.split('-').map(Number);
      f = { startDate: `${from}-01`, endDate: ymd(new Date(ty, tm, 0)) }; // laatste dag van 'to'-maand
    } else {
      f = filtersForPreset(periodKey);
    }
    getProvincieStats(f).then((d) => { if (alive) { setRows(Array.isArray(d) ? d : []); setLoading(false); } });
    return () => { alive = false; };
  }, [periodKey, cFrom, cTo]);

  const byProv = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => { m[r.provincie] = r; });
    return m;
  }, [rows]);

  const [mn, mx] = useMemo(() => {
    const vals = (rows || []).filter((r) => r.provincie !== 'Onbekend').map((r) => r[metric] || 0);
    return vals.length ? [Math.min(...vals), Math.max(...vals)] : [0, 1];
  }, [rows, metric]);

  // map aanmaken; ResizeObserver + invalidateSize zorgt dat de tiles renderen
  // zodra de container (binnen de net-geopende Pipeline-analyse) afmetingen krijgt
  useEffect(() => {
    if (!mapRef.current) return undefined;
    if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    const map = L.map(mapRef.current, {
      center: [52.15, 5.4], zoom: 7, minZoom: 6, maxZoom: 11,
      maxBounds: [[50.5, 2.8], [53.8, 7.6]], maxBoundsViscosity: 1.0,
      zoomControl: true, attributionControl: false, scrollWheelZoom: false,
    });
    L.tileLayer(cartoTileUrl(isDark), { maxZoom: 18 }).addTo(map);
    leafletRef.current = map;
    const ro = new ResizeObserver(() => { if (leafletRef.current) leafletRef.current.invalidateSize(); });
    ro.observe(mapRef.current);
    const t1 = setTimeout(() => map.invalidateSize(), 150);
    const t2 = setTimeout(() => map.invalidateSize(), 500);
    return () => { clearTimeout(t1); clearTimeout(t2); ro.disconnect(); map.remove(); leafletRef.current = null; };
  }, [isDark]);

  // choropleth-laag (her)tekenen
  useEffect(() => {
    const map = leafletRef.current;
    if (!map || !geo) return;
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }

    const colorFor = (r) => {
      if (!r) return isDark ? '#1e293b' : '#e2e8f0';
      if (metric === 'conversie') return r.conversie == null ? (isDark ? '#334155' : '#cbd5e1') : convColor(r.conversie);
      const v = r[metric] || 0;
      const t = mx > mn ? Math.pow((v - mn) / (mx - mn), 0.6) : 0.5;
      return rgb(lerp([147, 197, 253], [30, 58, 138], t)); // blue-300 -> blue-900
    };
    const tip = (r, naam) => {
      if (!r) return `<div class="prov-lf"><strong>${naam}</strong><br><span style="color:#94a3b8">geen data</span></div>`;
      const dtot = Object.values(r.diensten || {}).reduce((s, x) => s + x, 0) || 1;
      const brk = DIENST_ORDER.filter((k) => r.diensten && r.diensten[k]).slice(0, 4).map((k) => {
        const v = r.diensten[k];
        return `<div class="pr"><span class="nm">${k}</span><span class="ba"><i style="width:${Math.max(4, 100 * v / dtot).toFixed(0)}%"></i></span><span class="vv">${eurK(v)}</span></div>`;
      }).join('');
      const conv = r.conversie == null ? '—' : `${r.conversie}%`;
      return `<div class="prov-lf">
        <div class="tt">${naam}</div>
        <div class="kp">
          <div><b>${r.aanvragen}</b><span>aanvragen</span></div>
          <div><b>${r.facturen}</b><span>facturen</span></div>
          <div><b style="color:${convColor(r.conversie)}">${conv}</b><span>conversie</span></div>
          <div><b>${eurK(r.omzet)}</b><span>omzet</span></div>
        </div>
        ${brk ? `<div class="bh">omzet per type</div>${brk}` : ''}
        <div class="ft"><span>gem. factuur</span><b>${r.facturen ? eur(r.gemFactuur) : '—'}</b>${r.openstaand ? `<span class="op">openstaand ${eur(r.openstaand)}</span>` : ''}</div>
      </div>`;
    };
    const style = (f) => {
      const r = byProv[f.properties.naam];
      const isSel = selected === f.properties.naam;
      return {
        color: isSel ? '#D02E17' : (isDark ? '#475569' : '#64748b'),
        weight: isSel ? 3 : 0.8,
        fillColor: colorFor(r), fillOpacity: isDark ? 0.75 : 0.82, opacity: 1,
      };
    };
    const layer = L.geoJSON(geo, {
      style,
      onEachFeature: (f, lyr) => {
        lyr.bindTooltip(() => tip(byProv[f.properties.naam], f.properties.naam), { sticky: true, className: 'prov-lf-tip', opacity: 1 });
        lyr.on('mouseover', () => lyr.setStyle({ weight: 3, color: '#D02E17' }));
        lyr.on('mouseout', () => { if (selected !== f.properties.naam) layer.resetStyle(lyr); });
        lyr.on('click', () => setSelected((s) => (s === f.properties.naam ? null : f.properties.naam)));
      },
    }).addTo(map);
    layerRef.current = layer;
  }, [geo, rows, metric, selected, isDark, byProv, mn, mx]);

  const tableRows = (rows || []).filter((r) => r.provincie !== 'Onbekend')
    .slice().sort((a, b) => (metric === 'conversie' ? (b.conversie ?? -1) - (a.conversie ?? -1) : (b[metric] || 0) - (a[metric] || 0)));
  const tot = (rows || []).reduce((s, x) => ({ aanvragen: s.aanvragen + x.aanvragen, facturen: s.facturen + x.facturen, omzet: s.omzet + x.omzet }), { aanvragen: 0, facturen: 0, omzet: 0 });
  const totConv = tot.aanvragen ? Math.round((100 * tot.facturen) / tot.aanvragen) : 0;

  const thCss = { textAlign: 'right', padding: 'var(--space-2) var(--space-3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--color-neutral-500)', fontWeight: 'var(--font-medium)', borderBottom: '1px solid var(--color-neutral-200)' };
  const tdCss = { textAlign: 'right', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-neutral-100)', fontFamily: 'var(--font-mono)' };
  const totCss = { ...tdCss, fontWeight: 'var(--font-bold)', color: 'var(--color-neutral-900)', borderTop: '2px solid var(--color-neutral-200)' };
  const nowY = new Date().getFullYear();
  const periodPresets = [
    { k: 'all', label: 'Alles' },
    { k: '12m', label: 'Laatste 12 mnd' },
    { k: String(nowY), label: String(nowY) },
    { k: String(nowY - 1), label: String(nowY - 1) },
  ];
  const inputCss = { background: 'var(--color-neutral-100)', border: '1px solid var(--color-neutral-200)', color: 'var(--color-neutral-800)', borderRadius: 'var(--radius-md)', padding: '6px 9px', fontSize: '13px', fontFamily: 'var(--font-mono)', colorScheme: 'light dark' };

  return (
    <div className="card-blue" style={{ marginBottom: 'var(--space-6)' }}>
      <style>{`
        .prov-mtoggle .btn.active{background:var(--color-brand-600);color:#fff;border-color:var(--color-brand-600)}
        .leaflet-tooltip.prov-lf-tip{background:transparent;border:0;box-shadow:none;padding:0}
        .leaflet-tooltip.prov-lf-tip::before{display:none}
        .prov-lf{width:240px;background:var(--tooltip-bg,#fff);border:1px solid var(--tooltip-border,#e2e8f0);border-radius:var(--radius-lg);padding:12px 13px;color:var(--color-neutral-800);box-shadow:var(--shadow-lg);font-family:var(--font-body)}
        .prov-lf .tt{font-family:var(--font-display);font-weight:var(--font-semibold);font-size:15px;color:var(--color-neutral-900);display:flex;align-items:center;gap:6px}
        .prov-lf .kp{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--color-neutral-200);border:1px solid var(--color-neutral-200);border-radius:var(--radius-md);overflow:hidden;margin:9px 0}
        .prov-lf .kp>div{background:var(--tooltip-bg,#fff);padding:6px 9px}
        .prov-lf .kp b{font-family:var(--font-display);font-weight:var(--font-bold);font-size:16px;color:var(--color-neutral-900);display:block;line-height:1}
        .prov-lf .kp span{font-size:9px;letter-spacing:.04em;text-transform:uppercase;color:var(--color-neutral-500);display:block;margin-top:3px}
        .prov-lf .bh{font-size:9px;letter-spacing:.06em;text-transform:uppercase;color:var(--color-neutral-500);margin:2px 0 6px}
        .prov-lf .pr{display:grid;grid-template-columns:78px 1fr 40px;gap:6px;align-items:center;margin-bottom:4px;font-size:11px}
        .prov-lf .nm{color:var(--color-neutral-600);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .prov-lf .ba{height:5px;background:var(--color-neutral-100);border-radius:3px;overflow:hidden}
        .prov-lf .ba i{display:block;height:100%;background:var(--color-blue-500);border-radius:3px}
        .prov-lf .vv{font-family:var(--font-mono);font-size:10px;color:var(--color-neutral-700);text-align:right}
        .prov-lf .ft{display:flex;gap:8px;align-items:baseline;margin-top:8px;padding-top:7px;border-top:1px solid var(--color-neutral-200);font-size:11px;color:var(--color-neutral-500)}
        .prov-lf .ft b{color:var(--color-neutral-900);font-family:var(--font-display);font-weight:var(--font-semibold);font-size:13px}
        .prov-lf .ft .op{margin-left:auto;color:var(--color-brand-600)}
        .prov-tbl tr:hover td{background:var(--color-neutral-50)}
        .prov-tbl td.p{cursor:pointer}
      `}</style>

      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <h3 className="card-title" style={{ margin: 0 }}>Per provincie</h3>
          <InfoDot text="Facturen uit Moneybird, aanvragen uit de pijplijn, per provincie op basis van het klantadres. Beweeg over of klik op een provincie voor de details (omzet per type, gem. factuur, openstaand)." />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <div className="btn-group prov-mtoggle">
            {METRICS.map((m) => (
              <button key={m.k} className={`btn btn-sm ${metric === m.k ? 'active' : 'btn-ghost'}`} onClick={() => setMetric(m.k)}>{m.label}</button>
            ))}
          </div>
          <div className="btn-group prov-mtoggle">
            {periodPresets.map((p) => (
              <button key={p.k} className={`btn btn-sm ${(!cFrom && !cTo && periodKey === p.k) ? 'active' : 'btn-ghost'}`} onClick={() => { setCFrom(''); setCTo(''); setPeriodKey(p.k); }}>{p.label}</button>
            ))}
          </div>
          <input type="month" value={cFrom} onChange={(e) => setCFrom(e.target.value)} style={inputCss} aria-label="van maand" />
          <span style={{ color: 'var(--color-neutral-400)' }}>–</span>
          <input type="month" value={cTo} onChange={(e) => setCTo(e.target.value)} style={inputCss} aria-label="tot maand" />
        </div>
      </div>

      <div className="card-body">
        <div ref={mapRef} style={{ width: '100%', height: 440, borderRadius: 'var(--radius-md)', overflow: 'hidden', background: isDark ? '#090a0f' : '#fafaf8', position: 'relative', isolation: 'isolate', zIndex: 0 }} />

        {loading && !rows ? (
          <div style={{ marginTop: 'var(--space-4)', color: 'var(--color-neutral-500)', fontSize: 'var(--text-sm)' }}>Cijfers laden…</div>
        ) : (
        <>
              <div style={{ overflowX: 'auto', marginTop: 'var(--space-5)' }}>
                <table className="prov-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thCss, textAlign: 'left' }}>Provincie</th>
                      <th style={thCss}>Aanvragen</th><th style={thCss}>Facturen</th>
                      <th style={thCss}>Conversie</th><th style={thCss}>Omzet</th><th style={thCss}>Gem. factuur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((r) => (
                      <tr key={r.provincie} style={selected === r.provincie ? { background: 'var(--color-blue-50)' } : undefined}>
                        <td style={{ ...tdCss, textAlign: 'left', fontFamily: 'var(--font-body)', fontWeight: 'var(--font-semibold)', color: 'var(--color-neutral-900)' }} className="p" onClick={() => setSelected((s) => (s === r.provincie ? null : r.provincie))}>
                          {r.provincie}
                        </td>
                        <td style={tdCss}>{r.aanvragen.toLocaleString('nl-NL')}</td>
                        <td style={tdCss}>{r.facturen.toLocaleString('nl-NL')}</td>
                        <td style={{ ...tdCss, color: convColor(r.conversie), fontWeight: 'var(--font-semibold)' }}>{r.conversie == null ? '—' : `${r.conversie}%`}</td>
                        <td style={tdCss}>{eur(r.omzet)}</td>
                        <td style={tdCss}>{r.facturen ? eur(r.gemFactuur) : '—'}</td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...totCss, textAlign: 'left', fontFamily: 'var(--font-body)' }}>Totaal</td>
                      <td style={totCss}>{tot.aanvragen.toLocaleString('nl-NL')}</td>
                      <td style={totCss}>{tot.facturen.toLocaleString('nl-NL')}</td>
                      <td style={{ ...totCss, color: convColor(totConv) }}>{totConv}%</td>
                      <td style={totCss}>{eur(tot.omzet)}</td>
                      <td style={totCss} />
                    </tr>
                  </tbody>
                </table>
              </div>
        </>
        )}
      </div>
    </div>
  );
}
